import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchProducts } from '../api.js';
import { cn } from '../ui/cn.js';
import {
  Alert,
  Badge,
  Barcode,
  Button,
  EmptyState,
  FilePick,
  Input,
  PageHeader,
  Panel,
  PanelBody,
  ProgressBar,
  Table,
  TableShell,
  TBody,
  Td,
  Th,
  THead,
  Tr,
} from '../ui/index.js';
import { int, money, toCapitalizedCase } from '../lib/format.js';

interface IngestResult {
  readonly status: string;
  readonly created: number;
  readonly updated: number;
  readonly skipped: number;
  readonly failed: number;
}

type IngestPhase = 'idle' | 'upload' | 'processing';

/** S04 — Products (docs/09 §1): catalogue explorer + WooCommerce export ingest. */
export function ProductsPage(): React.ReactElement {
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();

  const urlSite = searchParams.get('site') ?? 'all';
  const urlStock = searchParams.get('stock') ?? 'all';
  const urlFlags = (searchParams.get('flags') ?? '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
  const urlQuery = searchParams.get('q') ?? searchParams.get('search') ?? '';

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState(urlQuery);
  const [query, setQuery] = useState(urlQuery);
  const [siteFilter, setSiteFilter] = useState<string>(urlSite);
  const [stockFilter, setStockFilter] = useState<string>(urlStock);
  const [flagFilters, setFlagFilters] = useState<readonly string[]>(urlFlags);

  useEffect(() => {
    const s = searchParams.get('site');
    if (s && s !== siteFilter) setSiteFilter(s);
    const stk = searchParams.get('stock');
    if (stk && stk !== stockFilter) setStockFilter(stk);
    const f = searchParams.get('flags');
    if (f) {
      const parsed = f.split(',').map((x) => x.trim()).filter(Boolean);
      setFlagFilters(parsed);
    }
    const q = searchParams.get('q') ?? searchParams.get('search');
    if (q !== null && q !== query) {
      setSearch(q);
      setQuery(q);
    }
  }, [searchParams]);
  const ingestInput = useRef<HTMLInputElement>(null);
  const [ingestPhase, setIngestPhase] = useState<IngestPhase>('idle');
  const [ingestProgress, setIngestProgress] = useState(0);
  const [ingestMessage, setIngestMessage] = useState<string | null>(null);
  const productsQuery = useQuery({
    queryKey: ['products', page, query, siteFilter, flagFilters, stockFilter],
    queryFn: () => fetchProducts(page, query, siteFilter, flagFilters, stockFilter),
  });

  const toggleFlag = (flag: string): void => {
    setFlagFilters((current) =>
      current.includes(flag) ? current.filter((f) => f !== flag) : [...current, flag],
    );
    setPage(1);
  };

  const ingestMutation = useMutation({
    mutationFn: async () => {
      const file = ingestInput.current?.files?.[0];
      if (file === undefined) throw new Error('choisissez un fichier');
      const form = new FormData();
      form.set('file', file);
      setIngestPhase('upload');
      setIngestProgress(0);
      return await new Promise<IngestResult>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', '/api/v1/ingest/wc-csv');
        xhr.responseType = 'json';
        xhr.withCredentials = true;
        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable) {
            setIngestProgress(Math.round((event.loaded / event.total) * 100));
          }
        };
        xhr.upload.onload = () => setIngestPhase('processing');
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) resolve(xhr.response as never);
          else
            reject(new Error((xhr.response as { error?: string })?.error ?? `HTTP ${xhr.status}`));
        };
        xhr.onerror = () => reject(new Error('réseau indisponible'));
        xhr.send(form);
      });
    },
    onSuccess: (result) => {
      setIngestPhase('idle');
      setIngestMessage(
        `Catalogue importé : ${result.created} créés, ${result.updated} mis à jour, ${result.skipped} inchangés, ${result.failed} rejetés.`,
      );
      void queryClient.invalidateQueries({ queryKey: ['products'] });
      void queryClient.invalidateQueries({ queryKey: ['kpis'] });
      if (ingestInput.current !== null) ingestInput.current.value = '';
    },
    onError: (error) => {
      setIngestPhase('idle');
      setIngestMessage(`Échec : ${String(error.message)}`);
    },
  });

  const data = productsQuery.data;
  const totalPages = data === undefined ? 1 : Math.max(1, Math.ceil(data.total / data.pageSize));
  const busy = ingestMutation.isPending;

  return (
    <div>
      <PageHeader
        index="S04"
        title="Produits"
        purpose="Explorateur du catalogue miroir. Chargez l'export WooCommerce (CSV ou XLSX) pour rafraîchir les 11 227 lignes de référence, puis cherchez par SKU, nom de produit ou marque."
        meta={[
          { label: 'Lignes miroir', value: data === undefined ? '—' : int(data.total) },
          { label: 'Page', value: `${page} / ${totalPages}` },
          { label: 'Filtre', value: query === '' ? 'aucun' : query },
        ]}
      />

      <Panel className="mb-4">
        <PanelBody className="flex flex-wrap items-center gap-3">
          <FilePick
            inputRef={ingestInput}
            accept=".csv,.xlsx,.xls"
            title="Export WooCommerce"
            hint="wc-product-export-*.csv ou .xlsx — l'ingestion remplace le miroir"
          />
          <Button
            type="button"
            variant="primary"
            size="md"
            className="ml-auto"
            disabled={busy}
            onClick={() => {
              setIngestMessage(null);
              ingestMutation.mutate();
            }}
          >
            {busy ? 'Ingestion…' : 'Ingérer le catalogue'}
          </Button>
          {busy && (
            <ProgressBar
              className="w-full max-w-md"
              value={ingestPhase === 'processing' ? null : ingestProgress}
              label={ingestPhase === 'processing' ? 'Analyse des lignes…' : 'Transfert du fichier'}
            />
          )}
        </PanelBody>
      </Panel>

      {ingestMessage !== null && (
        <Alert
          tone={ingestMessage.startsWith('Échec') ? 'alarm' : 'good'}
          title={ingestMessage.startsWith('Échec') ? 'Ingestion interrompue' : 'Ingestion terminée'}
          className="mb-4"
        >
          {ingestMessage}
        </Alert>
      )}

      <form
        className="mb-4 flex flex-wrap items-end gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          setPage(1);
          setQuery(search.trim());
        }}
      >
        <div className="w-full max-w-sm">
          <label
            htmlFor="products-search"
            className="label-narrow mb-1 block font-mono text-[10px] uppercase tracking-[0.16em] text-ink-soft"
          >
            Recherche catalogue
          </label>
          <Input
            id="products-search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="SKU, nom, marque…"
          />
        </div>
        <label className="flex flex-col gap-1 text-[10px] font-semibold uppercase tracking-wider text-ink-faint">
          État site
          <select
            value={siteFilter}
            onChange={(event) => {
              setSiteFilter(event.target.value);
              setPage(1);
            }}
            className="h-9 rounded-lg border border-border bg-card px-2.5 text-xs font-semibold text-ink shadow-xs"
          >
            <option value="all">Tous</option>
            <option value="published">Publiés & visibles</option>
            <option value="deleted">Hors ligne / Corbeille</option>
            <option value="hidden">Masqués (Woo)</option>
          </select>
        </label>
        <label className="flex flex-col gap-1 text-[10px] font-semibold uppercase tracking-wider text-ink-faint">
          Stock
          <select
            value={stockFilter}
            onChange={(event) => {
              setStockFilter(event.target.value);
              setPage(1);
            }}
            className="h-9 rounded-lg border border-border bg-card px-2.5 text-xs font-semibold text-ink shadow-xs"
          >
            <option value="all">Tous stocks</option>
            <option value="instock">En stock</option>
            <option value="outofstock">En rupture</option>
          </select>
        </label>
        <div className="flex flex-col gap-1 text-[10px] font-semibold uppercase tracking-wider text-ink-faint">
          <span>Flags</span>
          <div className="flex flex-wrap gap-1.5">
            {[
              { key: 'no-sku', label: 'Sans SKU' },
              { key: 'variant', label: 'Variantes' },
              { key: 'virtual', label: 'Virtuels' },
              { key: 'downloadable', label: 'Téléchargeables' },
            ].map((f) => (
              <button
                key={f.key}
                type="button"
                onClick={() => toggleFlag(f.key)}
                className={cn(
                  'rounded-full border px-3 py-1 text-[11px] font-semibold transition-colors',
                  flagFilters.includes(f.key)
                    ? 'border-herb-600 bg-herb-600 text-white'
                    : 'border-border bg-card text-ink-soft hover:border-border-strong',
                )}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
        <span className="flex-1" />
        <Button type="submit" variant="primary" size="md">
          Chercher
        </Button>
        {(query !== '' || siteFilter !== 'all' || stockFilter !== 'all' || flagFilters.length > 0) && (
          <Button
            type="button"
            variant="ghost"
            size="md"
            onClick={() => {
              setSearch('');
              setQuery('');
              setSiteFilter('all');
              setStockFilter('all');
              setFlagFilters([]);
              setPage(1);
            }}
          >
            Effacer
          </Button>
        )}
      </form>

      {productsQuery.isError && (
        <Alert tone="alarm" title="Catalogue indisponible" className="mb-4">
          {String(productsQuery.error.message)}
        </Alert>
      )}

      {data !== undefined && data.items.length === 0 && (
        <EmptyState
          title="Aucun produit pour ce filtre"
          hint="Effacez la recherche, ou ingérez l'export WooCommerce ci-dessus pour charger le catalogue miroir."
        />
      )}

      {data !== undefined && data.items.length > 0 && (
        <>
          <TableShell>
            <Table>
              <THead>
                <tr>
                  <Th>SKU</Th>
                  <Th>Nom</Th>
                  <Th>Marque</Th>
                  <Th>Type</Th>
                  <Th>GTIN</Th>
                  <Th align="right">Prix TTC</Th>
                  <Th align="right">Achat HT</Th>
                  <Th align="right">TVA</Th>
                </tr>
              </THead>
              <TBody>
                {data.items.map((product) => {
                  const gtin = product.barcodeRaw ?? product.eanRaw;
                  const offline = !product.isPublished;
                  return (
                    <Tr
                      key={product.id}
                      className={cn(offline && 'opacity-50 bg-paper/60')}
                    >
                      <Td className="font-mono text-xs text-ink-soft">{product.sku ?? '∅'}</Td>
                      <Td className="max-w-[26rem]">
                        <span className="block truncate text-ink">{toCapitalizedCase(product.name)}</span>
                        <div className="mt-1 flex flex-wrap gap-1">
                          {product.status === 'trash' && (
                            <Badge tone="alarm" className="mt-1">
                              corbeille Woo
                            </Badge>
                          )}
                          {product.status === 'draft' && (
                            <Badge tone="muted" className="mt-1">
                              brouillon Woo
                            </Badge>
                          )}
                          {product.status === 'private' && (
                            <Badge tone="muted" className="mt-1">
                              privé Woo
                            </Badge>
                          )}
                          {!product.isPublished &&
                            product.status !== 'trash' &&
                            product.status !== 'draft' &&
                            product.status !== 'private' && (
                              <Badge tone="alarm" className="mt-1">
                                hors ligne (miroir)
                              </Badge>
                            )}
                          {product.isPublished && !product.isVisible && (
                            <Badge tone="alarm" className="mt-1">
                              masqué du site
                            </Badge>
                          )}
                          {product.inStock === false && (
                            <Badge tone="alarm" className="mt-1">
                              rupture
                            </Badge>
                          )}
                          {product.isVariation && (
                            <Badge tone="scan" className="mt-1">
                              variante
                            </Badge>
                          )}
                          {product.wooVirtual === true && (
                            <Badge tone="signal" className="mt-1">
                              virtuel
                            </Badge>
                          )}
                          {product.wooDownloadable === true && (
                            <Badge tone="signal" className="mt-1">
                              téléchargeable
                            </Badge>
                          )}
                        </div>
                      </Td>
                      <Td className="text-ink-soft">{product.brand ?? '—'}</Td>
                      <Td>
                        <Badge tone={product.isBundle ? 'signal' : 'neutral'}>
                          {product.isBundle ? 'pack' : product.wcType}
                        </Badge>
                      </Td>
                      <Td>
                        {gtin === null || gtin === '' ? (
                          <span className="inline-flex items-center gap-2 text-ink-faint">
                            <Barcode value={null} />
                            <span className="font-mono text-xs">AUCUN</span>
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-2">
                            <Barcode value={gtin} tone="scan" />
                            <span className="tabular font-mono text-xs text-ink">{gtin}</span>
                          </span>
                        )}
                      </Td>
                      <Td align="right" className="tabular font-mono text-ink">
                        {money(product.salePriceTtc ?? product.regularPriceTtc)}
                      </Td>
                      <Td align="right" className="tabular font-mono text-ink-soft">
                        {product.purchasePriceHt === null ? '—' : money(product.purchasePriceHt)}
                      </Td>
                      <Td align="right">
                        {product.vatPercent === null ? (
                          <Badge tone="muted">N/A</Badge>
                        ) : (
                          <span className="inline-flex items-center gap-1.5">
                            <span className="tabular font-mono text-xs text-ink">
                              {product.vatPercent} %
                            </span>
                            {product.vatSourceLabel !== null && <Badge tone="scan">fichier</Badge>}
                          </span>
                        )}
                      </Td>
                    </Tr>
                  );
                })}
              </TBody>
            </Table>
          </TableShell>

          <div className="mt-3 flex flex-wrap items-center gap-3">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={page <= 1}
              onClick={() => setPage((previous) => previous - 1)}
            >
              ← Précédent
            </Button>
            <span className="tabular font-mono text-[10px] uppercase tracking-[0.14em] text-ink-soft">
              page {page} / {totalPages} · {int(data.total)} lignes
            </span>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={page >= totalPages}
              onClick={() => setPage((previous) => previous + 1)}
            >
              Suivant →
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
